import {
    Box,
    Chip,
    MenuItem,
    TextField,
    Typography,
    Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import countries from "world-countries";
import ReactCountryFlag from "react-country-flag";
import { formatAge, formatDescription, formatGender } from "../../utils/pearliUtils"

export default function FiltersPanel({ values, onChange, onClear }) {
    // Local state for immediate text field updates
    const [localJobTitle, setLocalJobTitle] = useState(values.jobTitle);
    // const [localCompany, setLocalCompany] = useState(values.company);
    // const [localInterest, setLocalInterest] = useState(values.interest);

    // Sync local state when external values change (e.g., clear filters)
    useEffect(() => {
        setLocalJobTitle(values.jobTitle);
    }, [values.jobTitle]);

    // useEffect(() => {
    //     setLocalCompany(values.company);
    // }, [values.company]);

    // useEffect(() => {
    //     setLocalInterest(values.interest);
    // }, [values.interest]);

    // Prepare country list
    const countryList = useMemo(() => {
        return countries
            .map((c) => ({
                name: c.name.common,
                code: c.cca2,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    const handleChange = (key) => (event) => {
        onChange(key, event.target.value);
    };

    // Debounced handlers for filter updates (not display)
    const debouncedJobTitleChange = useMemo(
        () => debounce((value) => onChange("job_title", value), 350),
        [onChange]
    );

    // const debouncedCompanyChange = useMemo(
    //     () => debounce((value) => onChange("company", value), 350),
    //     [onChange]
    // );

    // const debouncedInterestChange = useMemo(
    //     () => debounce((value) => onChange("interest", value), 350),
    //     [onChange]
    // );

    // Handle text input changes: update local state immediately + debounced filter
    const handleJobTitleChange = (e) => {
        const value = e.target.value;
        setLocalJobTitle(value);
        debouncedJobTitleChange(value);
    };

    // const handleCompanyChange = (e) => {
    //     const value = e.target.value;
    //     setLocalCompany(value);
    //     debouncedCompanyChange(value);
    // };

    // const handleInterestChange = (e) => {
    //     const value = e.target.value;
    //     setLocalInterest(value);
    //     debouncedInterestChange(value);
    // };

    // Cleanup debounced functions on unmount
    useEffect(() => {
        return () => {
            debouncedJobTitleChange.cancel();
            // debouncedCompanyChange.cancel();
            // debouncedInterestChange.cancel();
        };
    }, [
        debouncedJobTitleChange,
        // debouncedCompanyChange,
        // debouncedInterestChange,
    ]);

    const activeFilters = [
        { key: "age", label: formatAge(values.age) },
        { key: "gender", label: formatGender(values.gender) },
        { key: "description", label: formatDescription(values.description) },
        { key: "location", label: values.location },
        { key: "job_title", label: values.jobTitle },
        { key: "company", label: values.company },
        { key: "interest", label: values.interest },
    ].filter((f) => f.label && f.label !== "");

    return (
        <Box
            sx={{
                mt: 2,
                mb: 2,
                p: 2.5,
                borderRadius: 2,
                bgcolor: "background.paper",
                boxShadow: 1,
            }}
        >
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Participant Filters
            </Typography>

            {/* ACTIVE FILTER CHIPS */}
            {activeFilters.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                    {activeFilters.map((item) => (
                        <Chip
                            key={item.key}
                            label={item.label}
                            onDelete={() => onChange(item.key, "")}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: "0.7rem" }}
                        />
                    ))}

                    <Chip
                        label="Clear All"
                        onClick={onClear}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: "0.7rem", ml: 1 }}
                    />
                </Stack>
            )}

            {/* FILTER FIELDS */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                    },
                    gap: 2,
                }}
            >
                {/* AGE */}
                <TextField
                    label="Age"
                    select
                    size="small"
                    value={values.age}
                    onChange={handleChange("age")}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="under_18">Under 18</MenuItem>
                    <MenuItem value="18_24">18 - 24</MenuItem>
                    <MenuItem value="25_29">25 - 29</MenuItem>
                    <MenuItem value="30_34">30 - 34</MenuItem>
                    <MenuItem value="35_44">35 - 44</MenuItem>
                    <MenuItem value="45_55">45 - 55</MenuItem>
                    <MenuItem value="55_plus">55+</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </TextField>

                {/* GENDER */}
                <TextField
                    label="Gender"
                    select
                    size="small"
                    value={values.gender}
                    onChange={handleChange("gender")}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="non_binary">Non-binary</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </TextField>

                {/* BEST DESCRIPTION */}
                <TextField
                    label="Best Description of Yourself"
                    select
                    size="small"
                    value={values.description}
                    onChange={handleChange("description")}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="working_professional">Working Professional</MenuItem>
                    <MenuItem value="executive_leadership">Executive / Leadership</MenuItem>
                    <MenuItem value="caregiver_parent">Caregiver / Parent</MenuItem>
                    <MenuItem value="entrepreneur">Entrepreneur</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="athlete">Athlete</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </TextField>

                {/* LOCATION (Country Dropdown) */}
                <TextField
                    label="Country"
                    select
                    size="small"
                    value={values.location}
                    onChange={handleChange("location")}
                >
                    <MenuItem value="">All Countries</MenuItem>
                    {countryList.map((country) => (
                        <MenuItem key={country.code} value={country.name}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ReactCountryFlag
                                    countryCode={country.code}
                                    svg
                                    style={{
                                        width: "1.5em",
                                        height: "1.5em",
                                        marginRight: 8,
                                    }}
                                />
                                {country.name}
                            </Box>
                        </MenuItem>
                    ))}
                </TextField>

                {/* JOB TITLE */}
                <TextField
                    label="Job Title"
                    size="small"
                    value={localJobTitle}
                    onChange={handleJobTitleChange}
                    placeholder="e.g. Software Engineer"
                />

                {/* COMPANY */}
                {/* <TextField
                    label="Company"
                    size="small"
                    value={localCompany}
                    onChange={handleCompanyChange}
                    placeholder="e.g. Mindsherpa"
                /> */}

                {/* INTEREST */}
                {/* <TextField
                    label="Interest in Mindsherpa"
                    size="small"
                    value={localInterest}
                    onChange={handleInterestChange}
                    placeholder="High / Medium / Low"
                /> */}
            </Box>
        </Box>
    );
}